import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';
import { createAppTheme } from './theme/theme';
import { useChatApi } from './services/Chat/chatApi';
import { Chat } from './components/Chat';
import { Message, MessageStatus, ProgressMetadata } from './types/chat';
import { TrialState } from './types/trial';
import './styles/widget.css'; // Needed for widget mode(header-script)
import { AVAILABLE_LANGUAGES } from './constants/languages';
import { BASE_URL, FRONTEND_BASE_URL } from './constants/endpoints';
import { OrganizationData } from './types/organization';
import { Fade, Paper, Typography, IconButton } from '@mui/material';
import Close from '@mui/icons-material/Close';
import { OnboardingModal } from './components/Onboarding/OnboardingModal';
import { OnboardingProvider } from './context/OnboardingContext';
import { STORAGE_KEYS } from './constants/storage';
import { InvalidLinksProvider } from './context/InvalidLinksContext';
import { UsageStatsProvider } from './context/UsageStatsContext'; 
import { ORGANIZATION_ID_PATTERN } from './constants/patterns';
import SessionExpirationAlert from './components/SessionExpirationAlert';
import { 
  getTrialStatus, 
  ensureTrial,
  TrialStatusResponse 
} from './services/Trial/trialApi';

// Custom hook for URL change handling
function useUrlChangeHandler() {
  const [currentPathname, setCurrentPathname] = useState(window.location.pathname);

  useEffect(() => {
    const handleUrlChange = () => {
      const newPathname = window.location.pathname;
      if (newPathname !== currentPathname) {
        setCurrentPathname(newPathname);
      }
    };

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handleUrlChange);
    
    // Listen for pushState/replaceState
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(this, args);
      handleUrlChange();
    };
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(this, args);
      handleUrlChange();
    };
    
    // Cleanup
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [currentPathname]);

  return currentPathname;
}

function App() {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | undefined>();
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isUnauthenticated, setIsUnauthenticated] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { sendStreamingMessage } = useChatApi();
  const [isWidgetMode] = useState(() => 
    process.env.REACT_APP_BUILD_TYPE === 'widget'
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const [chatAssistantEnabled, setChatAssistantEnabled] = useState<boolean>(false);
  const [showWelcomeBubble, setShowWelcomeBubble] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [initData, setInitData] = useState<any>(null);
  const [progressData, setProgressData] = useState<ProgressMetadata | undefined>(undefined);
  const [organizationCache, setOrganizationCache] = useState<Record<string, boolean>>({});
  const [currentToken, setCurrentToken] = useState<string | null>(null);
  const [primaryOrgId, setPrimaryOrgId] = useState<string | null>(null);
  // The user's "operating set" of organization IDs (home org + filtered
  // memberships) exactly as AppManager's current_user returns it. Trial/overlay
  // decisions are driven from this set: the user-scoped per-org trial-status
  // call only succeeds for orgs in this set (APPMNGRAI-494).
  const [operatingOrgIds, setOperatingOrgIds] = useState<Set<string>>(new Set());
  
  // Trial state tracking
  const [trialStateCache, setTrialStateCache] = useState<Record<string, TrialState>>({});
  const [currentTrialState, setCurrentTrialState] = useState<TrialState>('loading');
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  // Trial banner state
  const [isTrial, setIsTrial] = useState<boolean>(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null);
  const [trialInfoCache, setTrialInfoCache] = useState<Record<string, { isTrial: boolean; daysRemaining: number | null }>>({});
  
  // Use the custom hook for URL changes
  const currentPathname = useUrlChangeHandler();
  
  // Listen for trial status changes from the API (e.g., when trial expires mid-session)
  useEffect(() => {
    const handleTrialStatusChange = (event: CustomEvent<{ code: string; message: string }>) => {
      const { code } = event.detail;
      
      if (code === 'trial_expired' || code === 'license_expired') {
        setCurrentTrialState('expired');
        // Update cache for current org
        if (currentOrgId) {
          setTrialStateCache(prev => ({
            ...prev,
            [currentOrgId]: 'expired'
          }));
        }
      } else if (code === 'no_license') {
        setCurrentTrialState('not_eligible');
        if (currentOrgId) {
          setTrialStateCache(prev => ({
            ...prev,
            [currentOrgId]: 'not_eligible'
          }));
        }
      }
    };
    
    window.addEventListener('trial-status-changed', handleTrialStatusChange as EventListener);
    
    return () => {
      window.removeEventListener('trial-status-changed', handleTrialStatusChange as EventListener);
    };
  }, [currentOrgId]);

  // Function to fetch and cache organization data
  const fetchAndCacheOrgData = async (orgId: string, token: string) => {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/organization/${orgId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const orgData = await response.json();
        // Update cache with just the chat_assistant_enabled flag
        setOrganizationCache(prev => ({
          ...prev,
          [orgId]: orgData.chat_assistant_enabled || false
        }));
        return orgData.chat_assistant_enabled || false;
      } else {
        console.error(`Failed to fetch data for organization ID ${orgId}`);
        return false;
      }
    } catch (error) {
      console.error('Error fetching organization data:', error);
      return false;
    }
  };

  // Function to determine trial state from status response
  const determineTrialState = (status: TrialStatusResponse): TrialState => {
    if (status.has_access) {
      return 'active';
    }
    if (status.trial_expired) {
      return 'expired';
    }
    // Check if eligible (no prior trial, no license)
    if (!status.chat_assistant_enabled && 
        status.license_type !== 'LICENSED' && 
        status.license_type !== 'TRIAL' && 
        status.license_type !== 'EXPIRED') {
      return 'eligible';
    }
    return 'not_eligible';
  };

  // Function to fetch and cache trial status for an organization
  const fetchAndCacheTrialStatus = async (orgId: string): Promise<TrialState> => {
    try {
      const status = await getTrialStatus(orgId);
      const trialState = determineTrialState(status);
      
      // Auto-activate trial silently for eligible orgs (new orgs post-bulk-activation)
      if (trialState === 'eligible') {
        try {
          const ensureResult = await ensureTrial(orgId);
          
          if (ensureResult.status === 'activated' || ensureResult.status === 'already_active') {
            // Update caches with new active state
            setTrialStateCache(prev => ({
              ...prev,
              [orgId]: 'active'
            }));
            
            setTrialInfoCache(prev => ({
              ...prev,
              [orgId]: {
                isTrial: ensureResult.is_trial,
                daysRemaining: ensureResult.trial_days_remaining ?? null
              }
            }));
            
            setIsTrial(ensureResult.is_trial);
            setTrialDaysRemaining(ensureResult.trial_days_remaining ?? null);
            
            // Refresh initData so /stream's initialize_session sees updated org data
            if (currentToken) {
              try {
                const [userRes, orgRes] = await Promise.all([
                  fetch(`${BASE_URL}/api/v1/users/current_user`, {
                    headers: { 'Authorization': `Bearer ${currentToken}` }
                  }),
                  fetch(`${BASE_URL}/api/v1/organization/${orgId}`, {
                    headers: { 'Authorization': `Bearer ${currentToken}` }
                  })
                ]);
                
                if (userRes.ok) {
                  const freshUserData = await userRes.json();
                  const orgEnabled = orgRes.ok ? (await orgRes.json()).chat_assistant_enabled ?? false : true;
                  
                  setOrganizationCache(prev => ({ ...prev, [orgId]: orgEnabled }));
                  setChatAssistantEnabled(orgEnabled);
                  
                  setInitData((prev: any) => ({
                    ...prev,
                    user_data: freshUserData,
                    organization_chat_enabled: {
                      ...prev?.organization_chat_enabled,
                      [orgId]: orgEnabled
                    }
                  }));
                }
              } catch (refreshErr) {
                console.error('Failed to refresh initData after trial activation:', refreshErr);
              }
            }
            
            return 'active';
          } else if (ensureResult.status === 'expired') {
            setTrialStateCache(prev => ({
              ...prev,
              [orgId]: 'expired'
            }));
            return 'expired';
          } else {
            setTrialStateCache(prev => ({
              ...prev,
              [orgId]: 'not_eligible'
            }));
            return 'not_eligible';
          }
        } catch (error) {
          console.error('Silent trial activation failed:', error);
          // Don't cache 'eligible' — return early without updating trialStateCache
          // so the next effect cycle re-attempts activation
          return 'eligible';
        }
      }
      
      setTrialStateCache(prev => ({
        ...prev,
        [orgId]: trialState
      }));
      
      // Cache trial info for banner display
      setTrialInfoCache(prev => ({
        ...prev,
        [orgId]: {
          isTrial: status.is_trial,
          daysRemaining: status.trial_days_remaining
        }
      }));
      
      // Update current trial info if this is the current org
      setIsTrial(status.is_trial);
      setTrialDaysRemaining(status.trial_days_remaining);
      
      return trialState;
    } catch (error) {
      console.error('Error fetching trial status:', error);
      // Default to not_eligible on error to avoid showing trial option incorrectly
      return 'not_eligible';
    }
  };

  useEffect(() => {
    // Clean up all feedback items from localStorage
    const cleanupFeedbackItems = () => {
      // Get all localStorage keys
      const allKeys = Object.keys(localStorage);
      
      // Filter keys that match the feedback pattern
      const feedbackKeys = allKeys.filter(key => 
        key.startsWith(STORAGE_KEYS.MESSAGE_FEEDBACK) || 
        key.startsWith('message_feedback')
      );
      
      // Remove all feedback items
      feedbackKeys.forEach(key => {
        localStorage.removeItem(key);
      });
    };
    
    // Run cleanup on initial load
    cleanupFeedbackItems();
    
    const getCookieValue = (name: string) => {
      const cookies = document.cookie.split('; ');
      const cookie = cookies.find(c => c.startsWith(name + '='));
      return cookie ? cookie.split('=')[1] : null;
    };
    
    const initializeToken = async (token: string) => {
      try {
        // First fetch user data
        const userResponse = await fetch(`${BASE_URL}/api/v1/users/current_user`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!userResponse.ok) {
          // If 401 Unauthorized, user is not authenticated - hide widget entirely
          if (userResponse.status === 401) {
            setIsUnauthenticated(true);
            setIsAuthenticating(false);
            return;
          }
          throw new Error('Failed to fetch user data');
        }

        const userData = await userResponse.json();
        
        // Store primary organization ID
        setPrimaryOrgId(userData.organization.id.toString());

        // Build the operating set of org IDs (home org + filtered memberships).
        // userData.organizations is already filtered by AppManager to the orgs
        // the user "operates on" (the same set the per-org access gate allows),
        // so any org outside it would 400 on the user-scoped trial call.
        const opIds = new Set<string>();
        if (userData?.organization?.id !== undefined && userData?.organization?.id !== null) {
          opIds.add(userData.organization.id.toString());
        }
        (userData.organizations || []).forEach((membership: any) => {
          const memberOrgId = membership?.organization?.id;
          if (memberOrgId !== undefined && memberOrgId !== null) {
            opIds.add(memberOrgId.toString());
          }
        });
        setOperatingOrgIds(opIds);
        
        // Initialize cache with primary organization data
        const primaryOrgEnabled = await fetchAndCacheOrgData(userData.organization.id.toString(), token);
        setOrganizationCache(prev => ({
          ...prev,
          [userData.organization.id.toString()]: primaryOrgEnabled
        }));

        // Fetch and cache data for all organizations user is part of
        const orgPromises = userData.organizations.map(async (org: any) => {
          const orgId = org.organization.id.toString();
          const enabled = await fetchAndCacheOrgData(orgId, token);
          return { orgId, enabled };
        });

        const orgResults = await Promise.all(orgPromises);
        const orgCache: Record<string, boolean> = {};
        orgResults.forEach(({ orgId, enabled }) => {
          orgCache[orgId] = enabled;
        });

        // Update the cache with all organization data
          setOrganizationCache(prev => ({
            ...prev,
          ...orgCache
          }));

        // Store token and initialization data in state
        const tokenData = { 
          access_token: token,
          user_data: userData,
          browser_language: navigator.language,
          frontend_base_url: FRONTEND_BASE_URL,
          organization_chat_enabled: orgCache // Add the cached organization data
        };
        
        setInitData(tokenData);
        setCurrentToken(token);
        localStorage.setItem(STORAGE_KEYS.WIDGET_AUTH_TOKEN, token);
      } catch (error) {
        console.error('Failed to initialize:', error);
        setError(t('authenticationFailed'));
      } finally {
        setIsAuthenticating(false);
      }
    };

    const authTokenRaw = getCookieValue('auth_token') || localStorage.getItem('auth_token');
    if (authTokenRaw) {
      try {
        const parsedToken = JSON.parse(authTokenRaw);
        const accessToken = parsedToken.access_token;
        if (accessToken) {
          initializeToken(accessToken);
        } else {
          // No access token in parsed data - user is not authenticated
          setIsUnauthenticated(true);
          setIsAuthenticating(false);
        }
      } catch (error) {
        console.error('Failed to parse auth token:', error);
        // Failed to parse token - user is not authenticated
        setIsUnauthenticated(true);
        setIsAuthenticating(false);
      }
    } else {
      // No auth token found - user is not authenticated
      setIsUnauthenticated(true);
      setIsAuthenticating(false);
    }
  }, [t]);

  useEffect(() => {
    // Force collapsed state initially in widget mode
    if (isWidgetMode) {
      setIsCollapsed(true);
    }
  }, [isWidgetMode]);

  useEffect(() => {
    // Auto-detect and set the user's language
    const userLanguage = navigator.language;
    const languageToSet = AVAILABLE_LANGUAGES.includes(userLanguage) ? userLanguage : 'en';
    i18n.changeLanguage(languageToSet);
  }, [i18n]);

  useEffect(() => {
    // Update welcome message when language changes
    setMessages([
      {
        id: '1',
        content: t('welcomeMessage'),
        timestamp: new Date(),
        sender: 'ai',
        status: MessageStatus.Sent,
        isMarkdown: true,
      } as Message,
    ]);
  }, [t]);

  useEffect(() => {
    // Only show welcome bubble if chat is collapsed and user hasn't seen it before
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcomeBubble');
    if (!hasSeenWelcome && isCollapsed) {
      setShowWelcomeBubble(true);
    }
  }, [isCollapsed]);

  // Effect to update chat enabled status and trial state when pathname changes
  useEffect(() => {
    const updateChatAndTrialState = async () => {
      if (!currentToken) return;

      const pathMatch = currentPathname.match(ORGANIZATION_ID_PATTERN);
      const orgIdFromPath = pathMatch ? pathMatch[1] : null;

      // APPMNGRAI-494: If the URL points to an organization outside the user's
      // operating set (e.g. MSP customer orgs reached via the account hierarchy,
      // or stale/cross-org navigation), the user-scoped per-org trial-status call
      // is rejected by AppManager (HTTP 400) and we'd surface a misleading
      // "Trial Not Available" overlay. Drive the decision from the operating set
      // instead: skip the per-org call and render a clean, no-overlay state. The
      // chat itself operates on the user's home/member orgs, not the URL org.
      if (
        orgIdFromPath &&
        operatingOrgIds.size > 0 &&
        !operatingOrgIds.has(orgIdFromPath)
      ) {
        setCurrentOrgId(orgIdFromPath);
        setChatAssistantEnabled(organizationCache[orgIdFromPath] ?? false);
        setCurrentTrialState('active');
        setIsTrial(false);
        setTrialDaysRemaining(null);
        return;
      }

      // Determine which org ID to use
      const effectiveOrgId = orgIdFromPath || primaryOrgId;
      setCurrentOrgId(effectiveOrgId);
      
      if (!effectiveOrgId) {
        setChatAssistantEnabled(false);
        setCurrentTrialState('loading');
        return;
      }

      // Update chat enabled status from cache
      if (organizationCache[effectiveOrgId] !== undefined) {
        setChatAssistantEnabled(organizationCache[effectiveOrgId]);
      } else {
        setChatAssistantEnabled(false);
      }

      // Update trial state - check cache first, then fetch if needed
      if (trialStateCache[effectiveOrgId] !== undefined) {
        setCurrentTrialState(trialStateCache[effectiveOrgId]);
        // Also update trial banner info from cache
        if (trialInfoCache[effectiveOrgId]) {
          setIsTrial(trialInfoCache[effectiveOrgId].isTrial);
          setTrialDaysRemaining(trialInfoCache[effectiveOrgId].daysRemaining);
        }
      } else if (!organizationCache[effectiveOrgId]) {
        // Only fetch trial status if org doesn't have access
        setCurrentTrialState('loading');
        const trialState = await fetchAndCacheTrialStatus(effectiveOrgId);
        setCurrentTrialState(trialState);
      } else {
        // Has access but no trial cache - fetch to check if it's a trial
        setCurrentTrialState('active');
        // Fetch trial status to check if it's a trial for the banner
        await fetchAndCacheTrialStatus(effectiveOrgId);
      }
    };

    updateChatAndTrialState();
  }, [currentToken, currentPathname, organizationCache, primaryOrgId, trialStateCache, trialInfoCache, operatingOrgIds]);

  const handleSendMessage = async (message: string) => {
    setError(undefined);
    
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content: message,
      timestamp: new Date(),
      sender: 'user',
      status: MessageStatus.Sending,
    };
    
    // Remove the initial message when sending first user message
    setMessages(prev => {
      if (prev.length === 1 && prev[0].sender === 'ai') {
        return [userMessage];
      }
      return [...prev, userMessage];
    });
    
    setIsTyping(true);
    const streamingMessageId = uuidv4();

    try {
        setIsStreaming(true);
        const streamingMessage: Message = {
          id: streamingMessageId,
          content: '',
          timestamp: new Date(),
          sender: 'ai',
          status: MessageStatus.Streaming,
          isMarkdown: true,
        };

        setMessages(prev => [
          ...prev.map(msg => 
            msg.id === userMessage.id ? { ...msg, status: MessageStatus.Sent } : msg
          ),
          streamingMessage
        ]);

        await sendStreamingMessage(
          message,
          (chunk, checkpoint_id, trace_id) => {
            setProgressData(undefined);
            
            setMessages(prev => prev.map(msg =>
              msg.id === streamingMessageId
                ? { 
                    ...msg,
                    trace_id: trace_id ? trace_id : msg.trace_id,
                    content: msg.content + chunk,
                    checkpoint_id: checkpoint_id
                  }
                : msg
            ));
          },
          (html, trace_id, error, checkpoint_id) => {
            if (error) {
              setError(error);
              // Hide progress just in case it's still showing
              setProgressData(undefined);
              setMessages(prev => prev.map(msg =>
                msg.id === streamingMessageId
                  ? { 
                      ...msg, 
                      status: MessageStatus.Error,
                      trace_id: trace_id ? trace_id : msg.trace_id,
                      checkpoint_id: checkpoint_id
                    }
                  : msg
              ));
            } else {
              setMessages(prev => prev.map(msg =>
                msg.id === streamingMessageId
                  ? { 
                      ...msg, 
                      status: MessageStatus.Sent, 
                      generated_html: html,
                      trace_id: trace_id ? trace_id : msg.trace_id,
                      checkpoint_id: checkpoint_id
                    }
                  : msg
              ));
            }
            setIsStreaming(false);
          },
          undefined,
          (newSuggestions: string[]) => {
            setSuggestions(newSuggestions);
          },
          initData,  // Always send initData with every message
          (waitingSentences, progressEstimate, trace_id, checkpoint_id) => {
            setProgressData({
              waitingSentences: waitingSentences || [],
              progressEstimate: progressEstimate || 0,
              trace_id: trace_id || '',
              checkpoint_id: checkpoint_id || '',
            });
          }
        );
    } catch (err) {
      setError(t('failedToSendMessage'));
      setMessages(prev => prev.map(msg =>
        msg.id === streamingMessageId || msg.status === MessageStatus.Streaming
          ? { ...msg, status: MessageStatus.Error } 
          : msg
      ));
    } finally {
      setIsTyping(false);
      setIsStreaming(false);
    }
  };

  const handleRetry = async (checkpointId: string, messageContent: string) => {
    setError(undefined);
    
    // Find the failed message
    const failedMessageIndex = messages.findIndex(msg => 
      msg.status === MessageStatus.Error && 
      msg.content === messageContent
    );
    
    if (failedMessageIndex === -1) return;
    
    // Find the user message before the failed message
    const userMessageIndex = failedMessageIndex - 1;
    if (userMessageIndex < 0) return;
    
    const userMessage = messages[userMessageIndex];
    
    // Keep messages up to and including the user message
    setMessages(messages.slice(0, userMessageIndex + 1));
    
    setIsTyping(true);

    const streamingMessageId = uuidv4();
    try {
      setIsStreaming(true);
      const streamingMessage: Message = {
        id: streamingMessageId,
        content: '',
        timestamp: new Date(),
        sender: 'ai',
        status: MessageStatus.Streaming,
        isMarkdown: true,
      };

      setMessages(prev => [...prev, streamingMessage]);

      await sendStreamingMessage(
        userMessage.content,
        (chunk, checkpoint_id, trace_id) => {
          setProgressData(undefined);
          setMessages(prev => prev.map(msg =>
            msg.id === streamingMessageId
              ? { 
                  ...msg, 
                  content: msg.content + chunk,
                  checkpoint_id: checkpoint_id,
                  trace_id: trace_id,
                }
              : msg
          ));
        },
        (html, trace_id, error, checkpoint_id) => {
          // Hide progress just in case it's still showing
          setProgressData(undefined);
          if (error) {
            setError(error);
            setMessages(prev => prev.map(msg =>
              msg.id === streamingMessageId
                ? { 
                    ...msg, 
                    status: MessageStatus.Error,
                    trace_id: trace_id,
                    checkpoint_id: checkpoint_id
                  }
                : msg
            ));
          } else {
            setMessages(prev => prev.map(msg =>
              msg.id === streamingMessageId
                ? { 
                    ...msg, 
                    status: MessageStatus.Sent, 
                    generated_html: html,
                    trace_id: trace_id,
                    checkpoint_id: checkpoint_id
                  }
                : msg
            ));
          }
          setIsStreaming(false);
        },
        checkpointId || '',  // Pass empty string if checkpointId is undefined
        (newSuggestions: string[]) => {
          setSuggestions(newSuggestions);
        },
        initData,
        (waitingSentences, progressEstimate, trace_id, checkpoint_id) => {
          setProgressData({
            waitingSentences: waitingSentences || [],
            progressEstimate: progressEstimate || 0,
            trace_id: trace_id || '',
            checkpoint_id: checkpoint_id || '',
          });
        }
      );
    } catch (err) {
      setError(t('failedToSendMessage'));
      setMessages(prev => prev.map(msg =>
        msg.id === streamingMessageId
          ? { ...msg, status: MessageStatus.Error }
          : msg
      ));
    } finally {
      setIsTyping(false);
      setIsStreaming(false);
    }
  };

  // Always show the widget - trial overlay handles the access state
  const handleDismissWelcome = () => {
    setShowWelcomeBubble(false);
    localStorage.setItem('hasSeenWelcomeBubble', 'true');
  };

  const handleToggleCollapse = (isOpenning?: boolean, mode?: 'chat' | 'sidepanel' | 'fullscreen') => {
    // If opening the chat, dismiss the welcome bubble permanently
    if (isCollapsed) {
      handleDismissWelcome();
    }
    setIsCollapsed(!isCollapsed);
    if (mode === 'sidepanel' && isOpenning) {
      // Send a message to the parent to tell it to adjust the dimesions to fit the widget
      window.dispatchEvent(new CustomEvent('appmanager-widget-dock-mode', { 
        detail: { docked: true } 
      }));
    }
  };

  // If user is not authenticated, don't render the widget at all
  if (isUnauthenticated) {
    return null;
  }

  return (
    <ThemeProvider theme={createAppTheme(true)}>
      {isCollapsed && showWelcomeBubble && (
        <Fade in={true}>
          <Paper
            sx={{
              position: 'fixed',
              bottom: '90px',
              right: '28px',
              padding: '12px 20px',
              borderRadius: '16px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              maxWidth: '260px',
              zIndex: 999999,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              background: 'linear-gradient(135deg, #1a237e 0%, #0d1657 100%)',
              color: 'white',
              animation: 'float 3s ease-in-out infinite',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                right: '32px',
                width: '16px',
                height: '16px',
                background: 'inherit',
                transform: 'rotate(45deg)',
                boxShadow: '4px 4px 12px rgba(0,0,0,0.08)',
              },
              '@keyframes float': {
                '0%, 100%': {
                  transform: 'translateY(0)',
                },
                '50%': {
                  transform: 'translateY(-5px)',
                },
              }
            }}
          >
            <Typography 
              variant="body2"
              sx={{ 
                fontSize: '0.95rem',
                fontWeight: 500,
                letterSpacing: '0.2px',
                lineHeight: 1.4,
              }}
            >
              {t('welcomeBubbleMessage')}
            </Typography>
            <IconButton
              size="small"
              onClick={handleDismissWelcome}
              sx={{
                padding: '4px',
                color: 'white',
                opacity: 0.85,
                transition: 'all 0.2s ease',
                '&:hover': {
                  opacity: 1,
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  transform: 'scale(1.1)',
                }
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Paper>
        </Fade>
      )}
      <UsageStatsProvider>
        <OnboardingProvider>
          <InvalidLinksProvider>
            <Chat
            messages={messages}
            onSendMessage={handleSendMessage}
            onRetry={handleRetry}
            isTyping={isTyping}
            isCollapsed={isCollapsed}
            onToggleCollapse={handleToggleCollapse}
            error={error}
            isStreaming={isStreaming}
            isAuthenticating={isAuthenticating}
            suggestions={suggestions}
            progressData={progressData}
            trialState={currentTrialState}
            isTrial={isTrial}
            trialDaysRemaining={trialDaysRemaining}
            currentOrganizationId={currentOrgId || undefined}
            />
            <OnboardingModal />
          </InvalidLinksProvider>
        </OnboardingProvider>
      </UsageStatsProvider>
      <SessionExpirationAlert />
    </ThemeProvider>
  );
}

export default App;